/**
 * The rail's model, and the section vocabulary the whole page keys on.
 *
 * ── THE ONE RULE THIS FILE ENFORCES ──────────────────────────────────────
 * A NAV LABEL AND THE HEADING IT SCROLLS TO ARE THE SAME STRING. Not similar,
 * the same. `SECTION_TITLE` is the single source, the rail reads it, and
 * `SectionHead` is passed the same value. On the panel this replaces, three
 * labels disagreed with their headings, and that is exactly the seam a senior
 * executive notices on first click.
 *
 * ── WHY THESE SIX, IN TWO GROUPS ──────────────────────────────────────────────────────
 * Two sections joined on Sep 9 2026 at Thomas's direction, and one left.
 *
 *   What students do, and Milestones and outcomes, carry what a student does
 *   on P3 without waiting for a match: opportunities opened and applied to,
 *   questions asked, Pulse AI, pathways explored, milestones completed and
 *   the outcome milestones students report. Every figure is a real query
 *   (in-app activity joined to student records, and the milestone records
 *   themselves), and every one of them suppresses under ten students. On a
 *   real first cohort most of them will be suppressed for a while, and the
 *   threshold state says so on its face rather than the panel being absent.
 *   That is the difference between "not yet" and "not built".
 *
 *   Mentor supply left. The national pool is not a fact about the
 *   institution's students, and the no-guarantee sentence it carried lives on
 *   the Security page, where a test asserts it.
 *
 *   Data and methods left the dashboard on Sep 9 2026 (Thomas): it belongs
 *   under Settings, which is planned, not on the page a VP reads weekly. The
 *   panel is parked in MethodsPanel.tsx for that route. Every figure still
 *   carries its provenance in its own trace drawer.
 *
 * Longitudinal outcomes, benchmarking and custom reports are still cut, for
 * the reasons `ABSENT_PANELS` states on the page. Sections are named after
 * the question the reader is asking, never after a P3 entity.
 */

import { createElement } from "react";
import type { ComponentType, SVGProps } from "react";

export type IconProps = SVGProps<SVGSVGElement>;
export type IconComponent = ComponentType<IconProps>;

/* ==========================================================================
   Section ids, in page order. The scroll-spy walks this array.

   Five since Sep 17 2026 (Thomas): Invitations left the scrolling page for
   its own screen, /admin/invitations (INVITATIONS_PAGE below), and Mentee
   Goals moved under the second group, so the dashboard reads Overview and
   then that group's four sections. The group is ACTIVITY since the same
   evening (Thomas: simpler than Performance). The Cohort group on the rail
   is two routes now, Invitations and Enrollment, and carries no section.
   ========================================================================== */

export const ADMIN_SECTIONS = [
  "overview",
  "mentorship",
  "engagement",
  "milestones",
  "mentee-goals",
] as const;

export type AdminSectionId = (typeof ADMIN_SECTIONS)[number];

/** The one place a section's name is written. Rail and heading both read it.
 *  Named by Thomas, Sep 9 2026 PM. */
export const SECTION_TITLE: Record<AdminSectionId, string> = {
  overview: "Overview",
  "mentee-goals": "Mentee Goals",
  mentorship: "Mentorship",
  engagement: "Engagement",
  milestones: "Milestones",
};

/* ONE LINE EACH. The lede sits in a 68ch measure, so anything past about 60
   characters wraps, and Thomas's Sep 9 note was that every header ran to two
   rows. A test holds the ceiling. */
export const SECTION_LEDE: Record<AdminSectionId, string> = {
  overview: "Your cohort at a glance, recomputed hourly.",
  "mentee-goals": "What students asked for at sign-up, and where they are headed.",
  mentorship: "How many students have a mentor, and how that compares.",
  engagement: "What your students do on P3, with or without a mentor.",
  milestones: "What students mark complete and report. Student reported.",
};

/**
 * The question each section answers. Shown in the command palette, so the
 * palette is a way to find an answer rather than a second copy of the menu.
 */
export const SECTION_QUESTION: Record<AdminSectionId, string> = {
  overview: "What does my cohort look like right now?",
  "mentee-goals": "What are my students asking for help with?",
  mentorship: "How many of my students have a mentor?",
  engagement: "How are my students using P3?",
  milestones: "What have my students completed and reported?",
};

/**
 * Invitations, its own screen since Sep 17 2026 (Thomas): the join link, the
 * code, the QR and the message to send, at /admin/invitations and
 * /demo/invitations. It was the dashboard's fifth section ("Roster
 * Registration", then "Invitations") until then, under four sections of
 * figures an owner had to scroll past to reach the link. The rail item and
 * the page heading read the same title, this file's one rule, and the
 * command palette offers it by its question the way it offers a section.
 */
export const INVITATIONS_PAGE = {
  title: "Invitations",
  lede: "Your join link, your code, and the invitation to send.",
  question: "How do I invite my students to join?",
} as const;

/* ==========================================================================
   Icons

   Inline, 20px, 1.6 stroke, currentColor. Self-hosted rather than pulled from
   an icon package: a paying institution's dashboard chrome does not depend on
   a third-party CDN, which is the same reason the P3 logo in the rail is
   served from /brand on this host and not from the marketing site's CDN.

   Built with `createElement` rather than JSX so this stays a `.ts` module: the
   nav model is data, and the page imports it from a `lib/` path.
   ========================================================================== */

function icon(path: string): IconComponent {
  return function Icon(props: IconProps) {
    return createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 1.6,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      focusable: "false",
      ...props,
      // A module-local literal, never caller input.
      dangerouslySetInnerHTML: { __html: path },
    });
  };
}

/** Overview: a small ledger of stacked rules. */
export const OverviewIcon = icon(
  '<rect x="3.2" y="4.2" width="17.6" height="15.6" rx="2.4"/><path d="M7 9h10M7 12.6h7M7 16.2h4"/>',
);

/** Enrollment: a person beside a list, the roster of who has joined. It was a
 *  card with a check until Sep 18 2026 (Thomas: clearer), which read as a
 *  form or as Billing's card rather than as people. */
export const RosterIcon = icon(
  '<circle cx="8" cy="8.2" r="2.8"/><path d="M3.4 18.6c0-2.6 2.1-4.4 4.6-4.4s4.6 1.8 4.6 4.4"/><path d="M15 7.6h5.6M15 11.6h5.6M15 15.6h4"/>',
);

/** Invitations: an envelope, the thing you send. It shared Enrollment's card
 *  and check until Sep 17 2026 (Thomas: two rail items, one glyph), which read
 *  as a second roster rather than the invitation to join one. */
export const InvitationsIcon = icon(
  '<rect x="3" y="5.6" width="18" height="12.8" rx="2.4"/><path d="M3.7 7.6l7.2 5.1a2 2 0 0 0 2.2 0l7.2-5.1"/>',
);

/** What students do: an activity pulse, the trace of use. */
export const ActivityIcon = icon(
  '<path d="M3.4 12.6h3.5l2.5-6.4 3.7 11.6 2.6-5.2h4.9"/>',
);

/** Milestones and outcomes: a flag on its pole, a checkpoint passed. */
export const MilestonesIcon = icon(
  '<path d="M6 20.8V3.6"/><path d="M6 4.4h11.6l-2.6 4.1 2.6 4.1H6"/>',
);

/** Mentorship: two people, one linked to the other. */
export const MentorshipIcon = icon(
  '<circle cx="8.6" cy="8.4" r="2.9"/><path d="M3.6 19.4c0-2.8 2.2-4.8 5-4.8s5 2 5 4.8"/><path d="M16.2 6.6a2.6 2.6 0 1 1 0 5.2"/><path d="M17 14.9c2 .5 3.4 2.2 3.4 4.5"/>',
);

/** What students need: a target, the thing they are aiming at. It was a raised
 *  hand until Sep 11 2026 (Thomas), which read as a tap rather than a goal. */
export const NeedsIcon = icon(
  '<circle cx="12" cy="12" r="8.4"/><circle cx="12" cy="12" r="4.4"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/>',
);

/** Settings. A route, not a section: /admin/settings and /demo/settings, where
 *  Data and methods, the provenance page, lives (Thomas, Sep 9 2026) rather
 *  than on the dashboard. The gear is Lucide's `settings` (ISC), read from
 *  the icon file rather than drawn here, since Sep 18 2026 (Thomas: the old
 *  one was distorted). The hand-adapted Feather gear it replaces had its top
 *  and right teeth about 1.6 units lower than the rest, so the teeth were
 *  unevenly spaced, and its outline ended 1.75 units below its start, closing
 *  with a straight vertical seam. Lucide's outline is twelve equal arcs of
 *  radius 2.34 on the same 24 grid, six teeth and six valleys, closed; the
 *  helper's 1.6 stroke stands in for its 2. */
export const SettingsIcon = icon(
  '<path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"/><circle cx="12" cy="12" r="3"/>',
);

/** Team and roles. A route, not a section: the members, an invitation, the roles. */
export const TeamIcon = icon(
  '<circle cx="9" cy="8.2" r="2.8"/><path d="M3.8 19.2c0-2.9 2.3-5 5.2-5s5.2 2.1 5.2 5"/><path d="M16.4 14.4h4.2M18.5 12.3v4.2"/>',
);

/** Log out, in the rail footer, where the member app keeps its own. */
export const LogOutIcon = icon(
  '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/>',
);

/** Billing. A route, not a section: the plan, the renewal control, the invoices. */
export const BillingIcon = icon(
  '<rect x="3" y="5.4" width="18" height="13.2" rx="2.4"/><path d="M3 9.8h18M6.8 14.6h3.4"/>',
);

/* ==========================================================================
   The rail
   ========================================================================== */

export interface AdminNavItem {
  /** Identical to the heading it scrolls to. */
  readonly label: string;
  readonly icon: IconComponent;
  /** In-page anchor. Exactly one of `section` or `to` is set. */
  readonly section?: AdminSectionId;
  /** A route outside the scrolling page, relative to the shell's base path
   *  (/admin or /demo), so one model serves both hosts. */
  readonly to?: string;
  /**
   * Set when the destination is specified but not built. It renders a `Planned`
   * chip and the item is not a link, because a nav item that navigates nowhere
   * is worse than one that says it is coming.
   */
  readonly planned?: boolean;
  /** The lowest role the route admits. A viewer does not see the item at all. */
  readonly minRole?: "admin" | "owner";
}

export interface AdminNavGroup {
  /** null renders the item with no group label above it. */
  readonly group: string | null;
  readonly items: readonly AdminNavItem[];
}

/**
 * Two groups under Overview, Thomas's Sep 9 split: COHORT is who the students
 * are and what they want (inputs), PERFORMANCE is what they did (outputs).
 * Sep 10 2026 (Thomas): Mentorship moved into PERFORMANCE, and PERFORMANCE
 * now comes before COHORT, on the rail and on the page. The reader opens on
 * what the subscription produced; who the students are and the tools to
 * reach the rest sit below it.
 */
export const ADMIN_NAV: readonly AdminNavGroup[] = [
  {
    group: null,
    items: [
      { label: SECTION_TITLE.overview, icon: OverviewIcon, section: "overview" },
    ],
  },
  {
    /* "Activity" since Sep 17 2026 (Thomas: simpler); "Performance" from Sep 9. */
    group: "Activity",
    items: [
      {
        label: SECTION_TITLE.mentorship,
        icon: MentorshipIcon,
        section: "mentorship",
      },
      {
        label: SECTION_TITLE.engagement,
        icon: ActivityIcon,
        section: "engagement",
      },
      {
        label: SECTION_TITLE.milestones,
        icon: MilestonesIcon,
        section: "milestones",
      },
      /* In this group since Sep 17 2026 (Thomas); it sat under Cohort
         from Sep 9. Last on the page, as on the rail. */
      {
        label: SECTION_TITLE["mentee-goals"],
        icon: NeedsIcon,
        section: "mentee-goals",
      },
    ],
  },
  {
    /* Two routes and no section since Sep 17 2026: who to invite, and who
       has joined. The tools to reach students, and the students reached. */
    group: "Cohort",
    items: [
      /* Its own screen since Sep 17 2026 (Thomas): /admin/invitations and
         /demo/invitations, the join link, the code, the QR and the message
         to send. Every role: a viewer can copy the link too. */
      { label: INVITATIONS_PAGE.title, icon: InvitationsIcon, to: "/invitations" },
      /* A real route since Sep 17 2026: /admin/enrollment and /demo/enrollment,
         the students and mentors who joined, by first name and last initial.
         Named Enrollment by Thomas the same day. Admin and owner only; the
         shell hides it from a viewer. */
      { label: "Enrollment", icon: RosterIcon, to: "/enrollment", minRole: "admin" },
      /* Matching (Sep 17 2026): a mentor for a student, asked from the
         portal and drawn as a rope. /admin/matching and /demo/matching.
         Admin and owner only, like Enrollment. */
      { label: "Matching", icon: MentorshipIcon, to: "/matching", minRole: "admin" },
    ],
  },
  {
    group: "Account",
    items: [
      /* A real route since Sep 9 2026, late: /admin/team and /demo/team. */
      { label: "Team and roles", icon: TeamIcon, to: "/team" },
      /* Billing left the rail on Sep 18 2026 (Thomas: fewer members
         fiddling with it). The route stays; Settings' Billing card is the
         way in, and the rail marks Settings while Billing is open. */
      /* A real route since Sep 9 2026 PM: /admin/settings and /demo/settings.
         The shell prefixes `to` with the page's base path. */
      { label: "Settings", icon: SettingsIcon, to: "/settings" },
    ],
  },
];

/** Every in-page item, flattened. The command palette and the spy read this. */
export const NAV_SECTION_ITEMS: readonly (AdminNavItem & {
  section: AdminSectionId;
})[] = ADMIN_NAV.flatMap((group) =>
  group.items.flatMap((item) =>
    item.section ? [{ ...item, section: item.section }] : [],
  ),
);
